import {
  Box,
  DialogContent,
  Divider,
  FormControl,
  FormControlLabel,
  FormLabel,
  TextField,
  IconButton,
  LinearProgress,
  Radio,
  RadioGroup,
  Stack,
  Typography,
  Dialog,
} from '@mui/material';
import {type ModalDialogProps, type PlanType} from 'interfaces';
import {type FC, useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';
import CloseIcon from '@mui/icons-material/Close';
import CheckIcon from '@mui/icons-material/Check';
import {SaveButton} from 'components/Generic/Buttons/SaveButton';
import {useGetPlan} from 'hooks/swr';
import Alert from 'helpers/Alert';
import backendCommunicationInstance from 'services/backendCommunicator';

export const PlanPublishModal: FC<ModalDialogProps<any, {id: string}>> = ({
  dialogProps,
  modalComponentProps: {
    formDataPayload: {id},
  },
}) => {
  const {t} = useTranslation('common');
  const [planName, setPlanName] = useState('');
  const [planComment, setPlanComment] = useState('');
  const {data: plan} = useGetPlan({resourceId: id});
  const [requestInProgress, setRequestInProgress] = useState(false);
  const [planType, setPlanType] = useState<PlanType>('harvest');

  const closeModal = (event) => dialogProps.onClose && dialogProps.onClose(event, 'escapeKeyDown');

  const publishPlan = async (event: any) => {
    try {
      setRequestInProgress(true);
      await backendCommunicationInstance.publishPlan(planType, planName, planComment, id);
      Alert.success(t('planPublished'));
      closeModal(event);
    } catch (error: any) {
      Alert.error(error);
    }
    setRequestInProgress(false);
  };

  const handleTypeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setPlanType((event.target as HTMLInputElement).value as PlanType);
  };

  useEffect(() => {
    if (plan) {
      setPlanName(plan.name);
    }
  }, [plan]);

  return (
    <Dialog {...dialogProps}>
      {(!plan || requestInProgress) && <LinearProgress color="info" sx={{height: 2}} />}
      <div style={{width: '100%'}}>
        <Box display="flex">
          <Box display="flex" m={1} p={1} flexGrow={1}>
            <Typography variant="h6"> {t('publishPlanToAPI')}</Typography>
          </Box>
          <Divider orientation="vertical" flexItem />
          <Box m={1}>
            <IconButton aria-label="close" onClick={closeModal} size="small">
              <CloseIcon color="secondary" />
            </IconButton>
          </Box>
        </Box>
      </div>
      <Divider />
      <DialogContent>
        <Stack m={0} spacing={3}>
          <TextField
            disabled={!plan}
            label={t('name')}
            onChange={({target: {value}}) => {
              setPlanName(value);
            }}
            value={planName}
            data-testid="publish-plan-name-input"
            fullWidth
          />
          <TextField
            disabled={!plan}
            label={t('comment')}
            onChange={({target: {value}}) => {
              setPlanComment(value);
            }}
            value={planComment}
            data-testid="publish-plan-comment-input"
            fullWidth
            multiline
            rows={2}
          />
          <FormControl
            sx={{
              width: 300,
            }}
          >
            <FormLabel id="publish-plan-radio-buttons-group-label-plantype">{t('planType')}</FormLabel>
            <RadioGroup
              aria-labelledby="publish-plan-radio-buttons-group-label"
              defaultValue={planType}
              onChange={handleTypeChange}
              name="publish-plan-radio-buttons-group-plantype"
            >
              <FormControlLabel value={'harvest'} control={<Radio />} label={t('harvest')} />
              <FormControlLabel
                value={'production'}
                control={<Radio />}
                label={t('production')}
                data-testid={'publish-production'}
              />
            </RadioGroup>
          </FormControl>
        </Stack>
        <Box display="flex" flexDirection="row-reverse" p={1}>
          <Box>
            <SaveButton
              disabled={!plan || planName.length === 0}
              loading={requestInProgress}
              onClick={publishPlan}
              endIcon={<CheckIcon />}
              data-testid="publish-plan-submit"
            >
              {t('publish')}
            </SaveButton>
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
};
